import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Utils from "../../../../utils";
import { postGoogleCalendarBookingLink, getGoogleCalendarBookingLink } from "../../../../services/CommonService";
import "./Calendar.css";

function Calendar() {
  const [loading, setLoading] = useState(false);
  const [bookingUrl, setBookingUrl] = useState("");
  const [fetchedBookingLink, setFetchedBookingLink] = useState(null);

  // ================ INITIALIZATION ================
  useEffect(() => {
    fetchBookingLink();
  }, []);

  // ================ API CALLS ================
  const fetchBookingLink = () => {
    setLoading(true);
    getGoogleCalendarBookingLink()
      .then((response) => {
        setFetchedBookingLink(response.data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(Utils.getErrorMessage(err));
      });
  };

  const handleSubmitBookingUrl = (e) => {
    e.preventDefault();
    if (!bookingUrl.trim()) {
      toast.error("Please enter a valid booking URL");
      return;
    }

    setLoading(true);
    postGoogleCalendarBookingLink({ booking_url: bookingUrl })
      .then((response) => {
        setLoading(false);
        toast.success(response.data.message || "Booking URL saved successfully");
        setBookingUrl("");
        setFetchedBookingLink({
          booking_url: bookingUrl,
          updatedAt: new Date().toISOString()
        });
      })
      .catch((err) => {
        setLoading(false);
        toast.error(Utils.getErrorMessage(err));
      });
  };

  // ================ UTILITY FUNCTIONS ================
  const handleCopyLink = () => {
    if (fetchedBookingLink?.booking_url) {
      navigator.clipboard.writeText(fetchedBookingLink.booking_url)
        .then(() => {
          toast.success("Booking URL copied to clipboard!");
        })
        .catch(() => {
          toast.error("Failed to copy URL");
        });
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // ================ RENDER ================
  return (
    <>
      {/* Loading Overlay */}
      {loading && <div className="page-loading">
        <img src="/images/loader.gif" alt="loader" />
      </div>}

      <div className="calendar-container">
        {/* Breadcrumbs */}
        <div className="calendar-breadcrumbs">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item text-white fs-5">Settings</li>
              <li className="breadcrumb-item active text-white fs-5" aria-current="page">Calendar</li>
            </ol>
          </nav>
        </div>

        {/* Main Content */}
        <div className="calendar-main-content">
          <div className="calendar-main-content-wrapper">
            <div className="calendar-header">
              <h1 className="calendar-title">
                <img src="/images/google_calendar.webp" alt="Google Calendar" />
                Google Calendar Integration
              </h1>
              <p className="calendar-subtitle">
                Set up your Google Calendar booking link to enable appointment scheduling
              </p>
            </div>

            {/* Integration Cards */}
            <div className="calendar-integration-section">
              <div className="row g-4">
                {/* Input Form Card */}
                <div className="col-lg-6">
                  <div className="calendar-card calendar-input-card">
                    <div className="calendar-card-header">
                      <div className="calendar-icon-wrapper">
                        <i className="bi bi-calendar-plus"></i>
                      </div>
                      <h3 className="calendar-card-title">Add Booking URL</h3>
                      <p className="calendar-card-subtitle">Enter your Google Calendar booking link</p>
                    </div>

                    <form onSubmit={handleSubmitBookingUrl} className="calendar-form">
                      <div className="calendar-input-group">
                        <label htmlFor="bookingUrl" className="calendar-label">
                          Google Calendar Booking URL
                        </label>
                        <div className="calendar-input-wrapper">
                          <i className="bi bi-link-45deg calendar-input-icon"></i>
                          <input
                            id="bookingUrl"
                            type="url"
                            className="calendar-input"
                            placeholder="https://calendar.google.com/calendar/appointments/..."
                            value={bookingUrl}
                            onChange={(e) => setBookingUrl(e.target.value)}
                            disabled={loading}
                          />
                        </div>
                      </div>

                      <button
                        type="submit"
                        className="calendar-submit-btn"
                        disabled={loading || !bookingUrl.trim()}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Saving...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-check-circle me-2"></i>
                            Save Booking URL
                          </>
                        )}
                      </button>
                    </form>
                  </div>
                </div>

                {/* Current Link Display Card */}
                <div className="col-lg-6">
                  <div className="calendar-card calendar-display-card">
                    <div className="calendar-card-header">
                      <div className="calendar-icon-wrapper">
                        <i className="bi bi-link-45deg"></i>
                      </div>
                      <h3 className="calendar-card-title">Current Booking Link</h3>
                    </div>

                    {fetchedBookingLink ? (
                      <div className="calendar-current-link">
                        <div className="calendar-link-status">
                          <i className="bi bi-check-circle-fill text-success me-2"></i>
                          <span className="calendar-status-text">Active booking link</span>
                        </div>

                        <div className="calendar-link-container">
                          <div className="calendar-link-wrapper">
                            <a
                              href={fetchedBookingLink.booking_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="calendar-link-text"
                              title="Open booking link"
                            >
                              {fetchedBookingLink.booking_url}
                            </a>
                            <button
                              className="calendar-copy-btn"
                              onClick={handleCopyLink}
                              title="Copy URL"
                            >
                              <i className="bi bi-clipboard"></i>
                            </button>
                          </div>
                        </div>

                        <div className="calendar-link-meta">
                          <small className="text-muted">
                            <i className="bi bi-clock me-1"></i>
                            Last updated: {formatDate(fetchedBookingLink.updatedAt)}
                          </small>
                        </div>
                      </div>
                    ) : (
                      <div className="calendar-empty-state">
                        <div className="calendar-empty-icon">
                          <i className="bi bi-calendar-x"></i>
                        </div>
                        <h4 className="calendar-empty-title">No Booking Link Set</h4>
                        <p className="calendar-empty-description">
                          Add your Google Calendar booking URL using the form on the left to enable appointment scheduling.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Step-by-Step Guide */}
          <div className="calendar-guide-section">
            <div className="calendar-guide-wrapper">
              <div className="calendar-guide-content">
                <div className="calendar-guide-header">
                  <h2 className="calendar-guide-title">
                    How to Get Your Google Calendar Booking Link
                  </h2>
                  <p className="calendar-guide-subtitle">
                    Follow these simple steps to create and copy your appointment scheduling link
                  </p>
                </div>

                <div className="calendar-steps-container">
                  <div className="calendar-steps-row">
                    {/* Step 1 */}
                    <div className="calendar-step-card">
                      <div className="calendar-step-number">1</div>
                      <div className="calendar-step-icon">
                        <i className="bi bi-calendar3"></i>
                      </div>
                      <h4 className="calendar-step-title">Open Google Calendar</h4>
                      <p className="calendar-step-description">
                        Navigate to <a href="https://calendar.google.com" target="_blank" rel="noopener noreferrer">https://calendar.google.com</a> and sign in to your Google account.
                      </p>
                    </div>

                    {/* Step 2 */}
                    <div className="calendar-step-card">
                      <div className="calendar-step-number">2</div>
                      <div className="calendar-step-icon">
                        <i className="bi bi-plus-circle"></i>
                      </div>
                      <h4 className="calendar-step-title">Create Appointment Schedule</h4>
                      <p className="calendar-step-description">
                        Click the <strong>"+"</strong> button, then select <strong>"Appointment schedule"</strong> from the dropdown menu.
                      </p>
                    </div>

                    {/* Step 3 */}
                    <div className="calendar-step-card">
                      <div className="calendar-step-number">3</div>
                      <div className="calendar-step-icon">
                        <i className="bi bi-gear"></i>
                      </div>
                      <h4 className="calendar-step-title">Configure Settings</h4>
                      <p className="calendar-step-description">
                        Set your availability, appointment duration, and other preferences for your booking page.
                      </p>
                    </div>
                  </div>

                  <div className="calendar-steps-row">
                    {/* Step 4 */}
                    <div className="calendar-step-card">
                      <div className="calendar-step-number">4</div>
                      <div className="calendar-step-icon">
                        <i className="bi bi-share"></i>
                      </div>
                      <h4 className="calendar-step-title">Get Sharing Link</h4>
                      <p className="calendar-step-description">
                        After saving, click the <strong>"Share"</strong> button to access your booking page link.
                      </p>
                    </div>

                    {/* Step 5 */}
                    <div className="calendar-step-card">
                      <div className="calendar-step-number">5</div>
                      <div className="calendar-step-icon">
                        <i className="bi bi-clipboard-check text-success"></i>
                      </div>
                      <h4 className="calendar-step-title">Copy & Paste Here</h4>
                      <p className="calendar-step-description">
                        Copy the booking link and paste it in the form above to integrate with your account.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Calendar;
